import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './Auth.css';
import './Game.css';
import './Work.css';
import AuthLayeredCard from './AuthLayeredCard';
import AuthSelectionToolbar from './AuthSelectionToolbar';
import { authButtonStyle } from '../constants/authStyles';
import { FISHING_MIN_LEVEL } from '../constants/fishing';
import { MINING_MIN_LEVEL } from '../constants/mining';
import { FARMING_MIN_LEVEL } from '../constants/farming';
import { CRAFT_MIN_LEVEL } from '../constants/craft';
import { getOnboardingPath } from '../utils/introVideo';
import { useCharacter } from '../context/CharacterContext';
import { useGameConfig } from '../context/GameConfigContext';
import { usePopupMessage } from '../hooks/usePopupMessage';
import { meetsMinLevel } from '../utils/levelRequirement';

const Work = () => {
  const { t } = useTranslation('selection');
  const { t: tc } = useTranslation('common');
  const { character: userCharacter, loading, hasCharacter } = useCharacter();
  const { gelistirmeConsole } = useGameConfig();
  const { setError } = usePopupMessage();
  const [brokenIcons, setBrokenIcons] = useState({});
  const navigate = useNavigate();
  const username = localStorage.getItem('username');

  const level = userCharacter?.level ?? 1;
  const canFish = meetsMinLevel(level, FISHING_MIN_LEVEL, gelistirmeConsole);
  const canMine = meetsMinLevel(level, MINING_MIN_LEVEL, gelistirmeConsole);
  const canFarm = meetsMinLevel(level, FARMING_MIN_LEVEL, gelistirmeConsole);
  const canCraft = meetsMinLevel(level, CRAFT_MIN_LEVEL, gelistirmeConsole);

  useEffect(() => {
    if (!username) {
      navigate('/login');
      return;
    }
    if (!loading && !hasCharacter) {
      navigate(getOnboardingPath(username), { replace: true });
    }
  }, [username, navigate, loading, hasCharacter]);

  const handleFishingClick = () => {
    if (!canFish) {
      setError(t('workFishingLocked', { level: FISHING_MIN_LEVEL }));
      return;
    }
    navigate('/fishing');
  };

  const handleMiningClick = () => {
    if (!canMine) {
      setError(t('workMiningLocked', { level: MINING_MIN_LEVEL }));
      return;
    }
    navigate('/mining');
  };

  const handleFarmingClick = () => {
    if (!canFarm) {
      setError(t('workFarmingLocked', { level: FARMING_MIN_LEVEL }));
      return;
    }
    navigate('/farming');
  };

  const handleCraftClick = () => {
    if (!canCraft) {
      setError(t('workCraftLocked', { level: CRAFT_MIN_LEVEL }));
      return;
    }
    navigate('/craft');
  };

  return (
    <div className="auth-container auth-container--selection work-page">
      <AuthLayeredCard className="work-card">
        <AuthSelectionToolbar
          onBack={() => navigate('/game')}
          backLabel={tc('back')}
          buttonStyle={authButtonStyle}
          title={t('workTitle')}
          subtitle={t('workSubtitle')}
        />

        <div className="auth-selection-body work-body">
          <div className="game-menu work-menu">
            <button
              type="button"
              className={`menu-button${canFish ? '' : ' menu-button--locked'}`}
              onClick={handleFishingClick}
              aria-disabled={!canFish}
            >
              {!brokenIcons.fishing ? (
                <img
                  className="menu-icon-image"
                  src="/image/menu/koy.png"
                  alt={t('workFishing')}
                  onError={() => setBrokenIcons((prev) => ({ ...prev, fishing: true }))}
                />
              ) : (
                <span className="menu-icon" aria-hidden="true">🎣</span>
              )}
              <span className="menu-text">{t('workFishing')}</span>
              <span className={`work-unlock-hint${canFish ? ' work-unlock-hint--open' : ''}`}>
                {canFish
                  ? (gelistirmeConsole && level < FISHING_MIN_LEVEL
                    ? t('workDevModeUnlocked')
                    : t('workFishingUnlocked'))
                  : t('workFishingUnlockHint', { level: FISHING_MIN_LEVEL })}
              </span>
            </button>
            <button
              type="button"
              className={`menu-button${canMine ? '' : ' menu-button--locked'}`}
              onClick={handleMiningClick}
              aria-disabled={!canMine}
            >
              {!brokenIcons.mining ? (
                <img
                  className="menu-icon-image"
                  src="/image/menu/koy.png"
                  alt={t('workMining')}
                  onError={() => setBrokenIcons((prev) => ({ ...prev, mining: true }))}
                />
              ) : (
                <span className="menu-icon" aria-hidden="true">⛏️</span>
              )}
              <span className="menu-text">{t('workMining')}</span>
              <span className={`work-unlock-hint${canMine ? ' work-unlock-hint--open' : ''}`}>
                {canMine
                  ? (gelistirmeConsole && level < MINING_MIN_LEVEL
                    ? t('workDevModeUnlocked')
                    : t('workMiningUnlocked'))
                  : t('workMiningUnlockHint', { level: MINING_MIN_LEVEL })}
              </span>
            </button>
            <button
              type="button"
              className={`menu-button${canFarm ? '' : ' menu-button--locked'}`}
              onClick={handleFarmingClick}
              aria-disabled={!canFarm}
            >
              {!brokenIcons.farming ? (
                <img
                  className="menu-icon-image"
                  src="/image/menu/koy.png"
                  alt={t('workFarming')}
                  onError={() => setBrokenIcons((prev) => ({ ...prev, farming: true }))}
                />
              ) : (
                <span className="menu-icon" aria-hidden="true">🌾</span>
              )}
              <span className="menu-text">{t('workFarming')}</span>
              <span className={`work-unlock-hint${canFarm ? ' work-unlock-hint--open' : ''}`}>
                {canFarm
                  ? (gelistirmeConsole && level < FARMING_MIN_LEVEL
                    ? t('workDevModeUnlocked')
                    : t('workFarmingUnlocked'))
                  : t('workFarmingUnlockHint', { level: FARMING_MIN_LEVEL })}
              </span>
            </button>
            <button
              type="button"
              className={`menu-button${canCraft ? '' : ' menu-button--locked'}`}
              onClick={handleCraftClick}
              aria-disabled={!canCraft}
            >
              {!brokenIcons.craft ? (
                <img
                  className="menu-icon-image"
                  src="/image/menu/koy.png"
                  alt={t('workCraft')}
                  onError={() => setBrokenIcons((prev) => ({ ...prev, craft: true }))}
                />
              ) : (
                <span className="menu-icon" aria-hidden="true">🔨</span>
              )}
              <span className="menu-text">{t('workCraft')}</span>
              <span className={`work-unlock-hint${canCraft ? ' work-unlock-hint--open' : ''}`}>
                {canCraft
                  ? (gelistirmeConsole && level < CRAFT_MIN_LEVEL
                    ? t('workDevModeUnlocked')
                    : t('workCraftUnlocked'))
                  : t('workCraftUnlockHint', { level: CRAFT_MIN_LEVEL })}
              </span>
            </button>
          </div>
        </div>
      </AuthLayeredCard>
    </div>
  );
};

export default Work;
